import { useState, useLayoutEffect, useMemo } from 'react';
import { isAndroid, isIOS } from 'react-device-detect';
import { useParams, useRouteMatch } from 'react-router-dom';
import qs from 'query-string';

import TextField from '@guestyci/foundation/TextField';
import createStyles from '@guestyci/foundation/createStyles';
import { Col, Row } from '@guestyci/foundation/Layout';
import defaultTheme from '@guestyci/foundation/theme/defaultTheme';
import { PermanentRoutes } from 'app/routes';
import logoSrc from 'assets/icons/logo/gfh-logo.svg';

import bgScreenSrc from 'assets/images/backgrounds/download-app-screen.png';
import bgScreen2xSrc from 'assets/images/backgrounds/download-app-screen@2x.png';
import bgScreen3xSrc from 'assets/images/backgrounds/download-app-screen@3x.png';

import loadingScreenSrc from 'assets/images/backgrounds/loading-screen.jpg';
import loadingScreen2xSrc from 'assets/images/backgrounds/loading-screen@2x.jpg';
import loadingScreen3xSrc from 'assets/images/backgrounds/loading-screen@3x.jpg';

import androidBtnSrc from 'assets/images/mobile_store/google_play.jpg';
import androidBtn2xSrc from 'assets/images/mobile_store/google_play@2x.jpg';
import androidBtn3xSrc from 'assets/images/mobile_store/google_play@3x.jpg';

import appleStoreBtn from 'assets/images/mobile_store/app_store.jpg';
import appleStore2xBtn from 'assets/images/mobile_store/app_store@2x.jpg';
import appleStore3xBtn from 'assets/images/mobile_store/app_store@3x.jpg';

import spinnerSrc from 'assets/spinner/spinner.gif';

import constant from 'constant';

const DOCUMENT_CLIENT_HEIGHT = document.documentElement.clientHeight;

const useStyles = createStyles((theme: typeof defaultTheme) => ({
  root: {
    padding: '3.5rem 2.7rem',
    justifyContent: 'space-between',
    height: DOCUMENT_CLIENT_HEIGHT,
  },
  text: {
    color: theme.palette.text.default,
    fontSize: '1rem',
  },
  btnImage: {
    height: 45,
    border: 0,
    backgroundColor: 'transparent',
    margin: '0 auto',
  },
  logoImg: {
    width: '8.75rem',
  },
  spinnerImg: {
    width: '7.85rem',
    height: '7.85rem',
  },
}));

const smallMobile = '(max-width: 320px)';
const mediumMobile = '(max-width: 640px)';

const TIMEOUT_DURATION = 1500;

function AndroidButton() {
  const classes = useStyles();

  return (
    <a href={constant.mobile.store.android} className={classes.btnImage}>
      <picture>
        <source srcSet={androidBtnSrc} media={smallMobile} />
        <source srcSet={androidBtn2xSrc} media={mediumMobile} />
        <img
          src={androidBtn3xSrc}
          alt="android"
          className="max-width-fill h-100"
        />
      </picture>
    </a>
  );
}

function AppleStoreButton() {
  const classes = useStyles();

  return (
    <a href={constant.mobile.store.apple} className={classes.btnImage}>
      <picture>
        <source srcSet={appleStoreBtn} media={smallMobile} />
        <source srcSet={appleStore2xBtn} media={mediumMobile} />
        <img
          src={appleStore3xBtn}
          alt="apple store"
          className="max-width-fill h-100"
        />
      </picture>
    </a>
  );
}

function LoadingScreen() {
  const classes = useStyles();

  return (
    <>
      <Row />
      <Col>
        <Row justify="center">
          <img src={spinnerSrc} className={classes.spinnerImg} alt="spinner" />
        </Row>
        <Row justify="center">
          <picture>
            <source srcSet={loadingScreen2xSrc} media={smallMobile} />
            <source srcSet={loadingScreen3xSrc} media={mediumMobile} />
            <img
              src={loadingScreenSrc}
              alt="loading"
              className="max-width-fill"
            />
          </picture>
        </Row>
        <Row justify="center" className="my-4">
          <TextField bold align="center" className={classes.text}>
            Logging into
            <br />
            Guesty for Hosts
          </TextField>
        </Row>
      </Col>

      <Row justify="center">
        <img className={classes.logoImg} src={logoSrc} alt="guesty for hosts" />
      </Row>
    </>
  );
}

function OpenAppInMobile() {
  const classes = useStyles();

  return (
    <>
      <Row justify="center" className="mb-12">
        <img className={classes.logoImg} src={logoSrc} alt="guesty for hosts" />
      </Row>

      <Row justify="center">
        <picture>
          <source srcSet={bgScreenSrc} media={smallMobile} />
          <source srcSet={bgScreen2xSrc} media={mediumMobile} />
          <img src={bgScreen3xSrc} className="max-width-fill" alt="home" />
        </picture>
      </Row>

      <Row justify="center" className="my-4">
        <TextField align="center" bold className={classes.text}>
          Download Guesty for Hosts’s mobile
          <br />
          app to continue
        </TextField>
      </Row>

      <Row justify="center">
        {isAndroid && <AndroidButton />}
        {isIOS && <AppleStoreButton />}
      </Row>
    </>
  );
}

declare global {
  interface Window {
    replaceUrlToMobile: (url: string) => void;
  }
}

// Cypress testing
window.replaceUrlToMobile = (url: string) => {
  window.location.replace(url);
};

function MobileComponent() {
  const [isLoading, setLoading] = useState(true);
  const classes = useStyles();
  const params = useParams();
  const match = useRouteMatch(PermanentRoutes[0].path);
  const easyLoginQueryStr = useMemo(() => {
    const easyLoginUrl = qs.stringifyUrl({
      url: `${constant.mobile.device}app/easy_login`,
      query: params,
    });
    return match ? easyLoginUrl : constant.mobile.device;
  }, [params, match]);

  useLayoutEffect(() => {
    // Note: There is a 1/2-second delay here to fix a flaky Cypress test that was
    // completely crashing Cypress by trying to run xdg-open. The delay gives Cypress
    // time to set up a mock before the function is called.
    const redirect = setTimeout(() => {
      window.replaceUrlToMobile(
        `${easyLoginQueryStr || constant.mobile.device}`
      );
    }, 500);

    const loader = setTimeout(() => {
      setLoading(false);
    }, TIMEOUT_DURATION + 500);

    return () => {
      clearTimeout(redirect);
      clearTimeout(loader);
    };
  }, [easyLoginQueryStr]);

  return (
    <Col className={classes.root}>
      {isLoading ? <LoadingScreen /> : <OpenAppInMobile />}
    </Col>
  );
}

export default MobileComponent;
